import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Unlock } from 'lucide-react'
import { useState } from 'react'
import { AButton, Callout, Panel, Section } from './Section'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { cn } from '../../lib/cn'
import { api, get } from '../../lib/api'
import { fmtScore, scoreTone } from '../../lib/score'
import type { AdminDashboard, JudgingGrid, SheetStatus } from './types'

/**
 * Slide 23's judge × booth matrix.
 * Every total here goes through fmtScore (1 decimal), exactly like the Judge
 * screen, so a judge's 87.5 reads 87.5 in the grid, in the drill-down and in Results.
 */

interface SheetDetail {
  sheetId: number
  status: SheetStatus
  locked: boolean
  rubric: { code: string; title: string }
  subject: { label: string; detail: string | null }
  kpis: { kpiId: number; seq: number; label: string; weight: number; mark: number | null; score: number | null }[]
  total: number
  submittedAt: string | null
  reopenedAt: string | null
}

const EMPTY_CELL: Record<Exclude<SheetStatus, 'submitted'>, string> = {
  in_progress: 'bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-300',
  not_started: 'bg-slate-100 text-slate-300 ring-1 ring-inset ring-slate-200',
}

const SHORT_AWARD: Record<string, string> = {
  sdg_project: 'SDG',
  sustainable_booth: 'Sustainable',
  entrepreneurship: 'Entrepreneurship',
  cultural_performance: 'Performance',
}

const shortAward = (code: string) => SHORT_AWARD[code] ?? code

/**
 * The matrix is drawn once per subject type, in the order a judge meets them.
 *
 * One table could not hold all three: the axes are genuinely different — 14
 * school booths, 16 entrepreneurship booths, 5 stage groups — and sharing a
 * header meant the widest axis labelled every column, so the entrepreneurship
 * row would have been read against booth numbers that are not its own.
 */
/*
 * One axis per kind of subject. Best SDG Project is marked against the school
 * booths and Best Sustainable Booth Practice against the entrepreneurship
 * booths, so both tables carry rows — `MatrixTable` skips any axis with none.
 */
const AXES = [
  { type: 'booth' as const, title: 'School booths', unit: 'booths' },
  { type: 'ent_booth' as const, title: 'Entrepreneurship booths', unit: 'booths E1–E16' },
  { type: 'performance_group' as const, title: 'Performance groups', unit: 'groups' },
]

export function JudgingSection({ live }: { live: { data: AdminDashboard | null; state: string } }) {
  const grid = live.data?.judging
  const [openSheet, setOpenSheet] = useState<number | null>(null)

  if (!grid) {
    return (
      <Section title="Judging progress">
        <p className="py-16 text-center text-sm text-slate-500">
          {live.state === 'error' ? 'Reconnecting to the live feed…' : 'Connecting…'}
        </p>
      </Section>
    )
  }

  const pct = grid.total ? (grid.submitted / grid.total) * 100 : 0

  // Per-judge progress, worked out from the grid cells.
  const perJudge = grid.judges.map((judge) => {
    const cells = grid.rows.filter((r) => r.judgeId === judge.id).flatMap((r) => r.cells)
    const done = cells.filter((c) => c.status === 'submitted').length
    return { judge, done, total: cells.length }
  })

  return (
    <Section
      title="Judging progress"
      description={`${grid.submitted} of ${grid.total} score sheets submitted · tap a cell to see the marking`}
    >
      <div className="space-y-3">
        {/* Overall + per judge */}
        <div className="grid gap-3 lg:grid-cols-4">
          <Panel className="lg:col-span-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">Overall</p>
            <p className="mt-1 text-3xl font-black tabular-nums text-[#0B1E3F]">
              {grid.submitted}
              <span className="text-lg font-bold text-slate-400"> / {grid.total}</span>
            </p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {grid.inProgress} in progress · {grid.total - grid.submitted - grid.inProgress} not started
            </p>
          </Panel>

          <div className="grid gap-3 sm:grid-cols-3 lg:col-span-3">
            {perJudge.map(({ judge, done, total }) => (
              <Panel key={judge.id}>
                <p className="truncate text-sm font-bold text-[#0B1E3F]">{judge.label}</p>
                <p className="truncate text-xs text-slate-500">{judge.name ?? ' '}</p>
                <div className="mt-2.5 flex items-baseline justify-between">
                  <span className="text-xl font-black tabular-nums text-[#0B1E3F]">
                    {done}
                    <span className="text-sm font-bold text-slate-400">/{total}</span>
                  </span>
                  <span className="text-xs font-bold tabular-nums text-slate-500">
                    {total ? Math.round((done / total) * 100) : 0}%
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full bg-orange-500 transition-[width] duration-500"
                    style={{ width: `${total ? (done / total) * 100 : 0}%` }}
                  />
                </div>
              </Panel>
            ))}
          </div>
        </div>

        {/* Matrix */}
        <Panel
          title="Judge × booth"
          description="Number = the judge's total out of 100"
          actions={
            <div className="hidden items-center gap-3 sm:flex">
              <Legend swatch="bg-emerald-500" label="Submitted" />
              <Legend swatch="bg-amber-200 ring-1 ring-amber-300" label="In progress" />
              <Legend swatch="bg-slate-100 ring-1 ring-slate-200" label="Not yet scored" />
            </div>
          }
          bodyClassName="p-0"
        >
          <MatrixTable grid={grid} onOpen={setOpenSheet} />
        </Panel>
      </div>

      <Dialog open={openSheet != null} onOpenChange={(open) => !open && setOpenSheet(null)}>
        <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto rounded-3xl p-0">
          {openSheet != null && <SheetDrilldown sheetId={openSheet} onClose={() => setOpenSheet(null)} />}
        </DialogContent>
      </Dialog>
    </Section>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('size-3 rounded', swatch)} />
      <span className="text-xs text-slate-500">{label}</span>
    </span>
  )
}

function MatrixTable({ grid, onOpen }: { grid: JudgingGrid; onOpen: (sheetId: number) => void }) {
  return (
    <div className="space-y-5 px-3 py-3 sm:px-4">
      {AXES.map((axis) => {
        const columns = grid.columns[axis.type] ?? []
        const rows = grid.rows.filter((r) => r.subjectType === axis.type)
        if (columns.length === 0 || rows.length === 0) return null

        return (
          <AxisTable
            key={axis.type}
            axis={axis}
            columns={columns}
            judges={grid.judges}
            rows={rows}
            onOpen={onOpen}
          />
        )
      })}
    </div>
  )
}

function AxisTable({
  axis,
  columns,
  judges,
  rows,
  onOpen,
}: {
  axis: (typeof AXES)[number]
  columns: JudgingGrid['columns'][keyof JudgingGrid['columns']]
  judges: JudgingGrid['judges']
  rows: JudgingGrid['rows']
  onOpen: (sheetId: number) => void
}) {
  const byJudge = judges.map((judge) => ({
    judge,
    rows: rows.filter((r) => r.judgeId === judge.id),
  }))

  return (
    <div>
      <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">
        {axis.title}
      </p>

      {/*
        A judge × booth grid is genuinely two-dimensional — it cannot become a
        stack of cards without ceasing to be a matrix — so this one scrolls
        sideways on a phone. What it must not do is scroll *silently*: the
        hint says so, and the row header stays pinned with a shadow at its
        edge so it reads as a frozen column rather than a rendering fault.
      */}
      <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400 lg:hidden">
        <span aria-hidden>↔</span>
        Scroll sideways for all {columns.length} {axis.unit}
      </p>

      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-0 text-left">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white pb-2 pr-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400 shadow-[6px_0_6px_-6px_rgba(15,23,42,0.18)]">
                Judge · Award
              </th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  title={c.detail}
                  className="min-w-[46px] pb-2 text-center text-xs font-bold tabular-nums text-slate-400"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {byJudge.map(({ judge, rows }, judgeIndex) =>
              rows.map((row, rowIndex) => {
                const gap = rowIndex === 0 && judgeIndex > 0 ? 'pt-4' : ''
                return (
                  <tr key={`${judge.id}-${row.rubricCode}`}>
                    <th
                      scope="row"
                      className={cn(
                        'sticky left-0 z-10 whitespace-nowrap bg-white py-1 pr-3 text-xs font-bold',
                        'shadow-[6px_0_6px_-6px_rgba(15,23,42,0.18)]',
                        gap,
                      )}
                    >
                      <span className="text-[#0B1E3F]">{judge.label}</span>
                      <span className="text-slate-300"> · </span>
                      <span className="font-medium text-slate-500">{shortAward(row.rubricCode)}</span>
                    </th>

                    {columns.map((col, i) => {
                      const cell = row.cells[i]
                      if (!cell) return <td key={col.key} className={cn('py-1', gap)} />

                      const subject =
                        row.subjectType === 'performance_group'
                          ? `Group ${cell.label}`
                          : `Booth ${cell.label}`

                      const title = `${judge.label} · ${row.rubricTitle} · ${subject}${
                        cell.total != null ? ` — ${fmtScore(cell.total)}` : ''
                      }`

                      const submitted = cell.status === 'submitted' && cell.total != null

                      return (
                        <td key={col.key} className={cn('px-0.5 py-1', gap)}>
                          <button
                            type="button"
                            title={title}
                            aria-label={title}
                            disabled={cell.sheetId == null}
                            onClick={() => cell.sheetId != null && onOpen(cell.sheetId)}
                            className={cn(
                              // 44px tall on touch, the minimum comfortable tap
                              // target; back to 32px where there is a mouse.
                              'grid h-11 w-full place-items-center rounded-lg text-[11px] font-bold tabular-nums transition-transform sm:h-8',
                              'hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50',
                              submitted
                                ? scoreTone(cell.total as number)
                                : EMPTY_CELL[cell.status === 'in_progress' ? 'in_progress' : 'not_started'],
                            )}
                          >
                            {submitted ? fmtScore(cell.total) : ''}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                )
              }),
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** One judge's marking in full, and the re-open control (the only route back into a locked sheet). */
function SheetDrilldown({ sheetId, onClose }: { sheetId: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sheet = useQuery({
    queryKey: ['admin-sheet', sheetId],
    queryFn: () => get<{ sheet: SheetDetail }>(`/api/admin/sheets/${sheetId}`, 'staff'),
  })

  const reopen = useMutation({
    mutationFn: () => api(`/api/admin/sheets/${sheetId}/reopen`, { method: 'POST', auth: 'staff' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sheet', sheetId] })
      setConfirming(false)
      onClose()
    },
    onError: () => {
      setConfirming(false)
      setError('Could not re-open the sheet. It may already be open.')
    },
  })

  if (sheet.isLoading) return <p className="p-10 text-center text-sm text-slate-500">Loading the marking…</p>
  if (!sheet.data) return <p className="p-10 text-center text-sm text-slate-500">Sheet not found.</p>

  const s = sheet.data.sheet

  return (
    <div className="px-5 pb-5 pt-6">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-orange-600">{s.rubric.title}</p>
      <DialogTitle className="mt-0.5 text-xl font-extrabold text-[#0B1E3F]">{s.subject.label}</DialogTitle>
      <DialogDescription className="mt-0.5 text-sm text-slate-500">
        {s.subject.detail}
        {s.submittedAt &&
          ` · submitted ${new Date(s.submittedAt).toLocaleString([], {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}`}
      </DialogDescription>

      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Judge total</p>
          <p className="text-3xl font-black leading-none tabular-nums text-[#0B1E3F]">
            {fmtScore(s.total)}
            <span className="text-sm font-semibold text-slate-400"> / 100</span>
          </p>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold',
            s.locked ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700',
          )}
        >
          {s.locked ? <Lock className="size-3" /> : <Unlock className="size-3" />}
          {s.locked ? 'Locked' : s.status === 'in_progress' ? 'In progress' : 'Not started'}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
        <span>KPI</span>
        <span className="w-8 text-right">Wt</span>
        <span className="w-8 text-right">Mark</span>
        <span className="w-11 text-right">Score</span>
      </div>
      <ul className="mt-1 divide-y divide-slate-100 text-sm">
        {s.kpis.map((kpi) => (
          <li key={kpi.kpiId} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 px-1 py-2">
            <span className="min-w-0 text-[13px] leading-snug text-slate-700">
              <span className="text-slate-400">{kpi.seq}. </span>
              {kpi.label}
            </span>
            <span className="w-8 text-right text-xs text-slate-400">{kpi.weight}</span>
            <span className="w-8 text-right font-bold tabular-nums text-[#0B1E3F]">{kpi.mark ?? '—'}</span>
            <span className="w-11 text-right font-bold tabular-nums text-orange-600">{fmtScore(kpi.score)}</span>
          </li>
        ))}
      </ul>

      {error && <Callout tone="error" className="mt-3">{error}</Callout>}

      {s.locked ? (
        confirming ? (
          <>
            <Callout className="mt-3 text-xs">
              Re-opening lets {s.rubric.title.toLowerCase()} be marked again for {s.subject.label}. The judge is
              told the sheet was re-opened, and this is recorded in the audit log.
            </Callout>
            <div className="mt-3 flex gap-2.5">
              <AButton tone="outline" block disabled={reopen.isPending} onClick={() => setConfirming(false)} autoFocus>
                Keep it locked
              </AButton>
              <AButton tone="navy" block disabled={reopen.isPending} onClick={() => reopen.mutate()}>
                {reopen.isPending ? 'Re-opening…' : 'Re-open sheet'}
              </AButton>
            </div>
          </>
        ) : (
          <AButton tone="outline" block className="mt-4" onClick={() => setConfirming(true)}>
            <Unlock className="size-4" strokeWidth={2.2} />
            Re-open this sheet
          </AButton>
        )
      ) : (
        <AButton block className="mt-4" onClick={onClose}>
          Close
        </AButton>
      )}
    </div>
  )
}
